<template>
  <div class="relative flex min-h-screen flex-col items-center justify-center overflow-x-hidden bg-slate-100 px-4 pb-8 pt-16 dark:bg-gray-950 sm:px-6 md:py-8">
    <!-- Fundo: malha discreta + brilhos nas cores da marca (sem canvas/animação por frame) -->
    <div class="pointer-events-none fixed inset-0" aria-hidden="true">
      <div
        class="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,64,128,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,64,128,0.07)_1px,transparent_1px)] bg-[size:36px_36px] [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_75%)] dark:bg-[linear-gradient(to_right,rgba(32,184,232,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(32,184,232,0.08)_1px,transparent_1px)]"
      ></div>
      <div class="absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-brand-cyan/25 blur-3xl dark:bg-brand-cyan/10"></div>
      <div class="absolute -bottom-40 -right-24 h-[32rem] w-[32rem] rounded-full bg-brand-navy/15 blur-3xl dark:bg-brand-navy/40"></div>
    </div>

    <!-- Alternar tema -->
    <button
      type="button"
      class="fixed right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/80 text-gray-600 shadow-sm backdrop-blur transition hover:bg-white hover:text-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
      :aria-label="isDark ? 'Ativar tema claro' : 'Ativar tema escuro'"
      @click="toggleDarkMode"
    >
      <Sun v-if="isDark" class="h-5 w-5" />
      <Moon v-else class="h-5 w-5" />
    </button>

    <!-- Cartão principal -->
    <div
      class="relative z-10 w-full max-w-5xl animate-fade-in-up overflow-hidden rounded-2xl bg-white shadow-2xl shadow-brand-navy/15 ring-1 ring-black/5 dark:bg-gray-900 dark:shadow-black/50 dark:ring-white/10 md:grid md:min-h-[640px] md:grid-cols-[1.05fr_1fr]"
    >
      <!-- Painel institucional (desktop) -->
      <section class="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-navy via-[#003670] to-brand-deep px-8 py-8 text-white md:flex lg:px-12 lg:py-9">
        <div class="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(32,184,232,0.28),transparent_55%)]" aria-hidden="true"></div>

        <!-- Motivo decorativo: anéis de um gantry de tomografia -->
        <svg
          class="pointer-events-none absolute -bottom-28 -right-28 h-[26rem] w-[26rem] text-white opacity-[0.08] lg:h-[30rem] lg:w-[30rem]"
          viewBox="0 0 400 400"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="200" cy="200" r="192" stroke="currentColor" stroke-width="2" />
          <circle cx="200" cy="200" r="156" stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 12" class="origin-center animate-[spin_80s_linear_infinite]" />
          <circle cx="200" cy="200" r="118" stroke="currentColor" stroke-width="2" />
          <circle cx="200" cy="200" r="72" stroke="currentColor" stroke-width="1" />
          <path d="M200 8v384M8 200h384" stroke="currentColor" stroke-width="1" />
        </svg>

        <div class="relative">
          <img :src="saudeLogo" alt="Super Centro Carioca de Saúde · Saúde Pública Carioca" class="h-9 w-auto object-contain lg:h-10" />

          <span class="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-cyan">
            <span class="h-1.5 w-1.5 rounded-full bg-brand-cyan"></span>
            Plataforma de gestão
          </span>

          <h1 class="mt-5 text-3xl font-bold leading-tight tracking-tight lg:text-4xl">
            Gestão &amp; Controle da rede de diagnóstico por imagem
          </h1>
          <p class="mt-4 max-w-md text-sm leading-relaxed text-blue-100/85 lg:text-base">
            Sistema integrado do Super Centro Carioca de Saúde e unidades avançadas: operação, equipamentos e cuidado em um só lugar.
          </p>

          <ul class="mt-7 space-y-3">
            <li v-for="feature in features" :key="feature.title" class="flex items-start gap-3.5">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/10 text-brand-cyan">
                <component :is="feature.icon" class="h-[18px] w-[18px]" />
              </span>
              <div>
                <p class="text-sm font-semibold leading-snug">{{ feature.title }}</p>
                <p class="mt-0.5 text-[13px] leading-snug text-blue-100/75">{{ feature.text }}</p>
              </div>
            </li>
          </ul>
        </div>

        <div class="relative mt-8 flex items-end justify-between gap-6">
          <p class="max-w-xs text-xs leading-relaxed text-blue-100/70">
            Apoio completo à tomada de decisão, segurança assistencial e eficiência operacional em toda a rede.
          </p>
          <img :src="zegotinhaImage" alt="Zé Gotinha" class="h-24 w-auto animate-float object-contain drop-shadow-[0_14px_20px_rgba(0,0,0,0.35)] lg:h-28" />
        </div>
      </section>

      <!-- Faixa institucional (mobile) -->
      <div class="flex items-center justify-between bg-gradient-to-r from-brand-navy to-brand-deep px-6 py-4 md:hidden">
        <img :src="saudeLogo" alt="Super Centro Carioca de Saúde · Saúde Pública Carioca" class="h-6 w-auto max-w-[calc(100%-3.5rem)] object-contain object-left" />
        <img :src="zegotinhaImage" alt="" class="h-11 w-auto shrink-0 object-contain drop-shadow-lg" />
      </div>

      <!-- Painel de acesso -->
      <section class="flex flex-col px-6 py-8 sm:px-10 sm:py-10 lg:px-14">
        <div class="flex h-12 items-center justify-center">
          <Transition mode="out-in" enter-active-class="transition-opacity duration-300" leave-active-class="transition-opacity duration-200" enter-from-class="opacity-0" leave-to-class="opacity-0">
            <img :key="isDark ? 'dark' : 'light'" :src="isDark ? logoCcdtiBranco : logoCcdti" alt="CCDTI" class="w-auto object-contain" :class="isDark ? 'h-14' : 'h-11'" />
          </Transition>
        </div>

        <div class="flex flex-1 flex-col justify-center py-8">
          <div class="text-center md:text-left">
            <h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Acesse o sistema</h2>
            <p class="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
              Entre com suas credenciais institucionais para utilizar os módulos de gestão e controle.
            </p>
          </div>

          <!-- Mensagens -->
          <div class="mt-6 space-y-3 empty:hidden">
            <transition name="slide-down">
              <div v-if="errorMessage" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300" role="alert">
                <CircleAlert class="mt-0.5 h-4 w-4 shrink-0" />
                <span>{{ errorMessage }}</span>
              </div>
            </transition>
            <transition name="slide-down">
              <div v-if="successMessage" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300" role="status">
                <CircleCheck class="mt-0.5 h-4 w-4 shrink-0" />
                <span>{{ successMessage }}</span>
              </div>
            </transition>
          </div>

          <!-- Formulário -->
          <form class="mt-6" novalidate @submit.prevent="handleLogin">
            <div class="space-y-5">
              <div>
                <label for="username" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Usuário ou e-mail</label>
                <div class="relative">
                  <User class="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    id="username"
                    v-model="username"
                    type="text"
                    autocomplete="username"
                    autocapitalize="none"
                    spellcheck="false"
                    required
                    class="h-11 w-full rounded-lg border-gray-300 bg-white pl-11 pr-4 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-cyan/40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-brand-cyan"
                    placeholder="Digite seu usuário"
                  />
                </div>
              </div>

              <div>
                <div class="mb-1.5 flex items-center justify-between">
                  <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Senha</label>
                  <button type="button" class="text-xs font-medium text-brand-navy transition hover:underline dark:text-brand-cyan" @click="handleForgotPassword">
                    Esqueci minha senha
                  </button>
                </div>
                <div class="relative">
                  <Lock class="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    id="password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="current-password"
                    required
                    class="h-11 w-full rounded-lg border-gray-300 bg-white pl-11 pr-11 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-cyan/40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-brand-cyan"
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    class="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 transition hover:text-brand-navy focus:outline-none focus-visible:text-brand-navy dark:hover:text-brand-cyan"
                    :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                    @click="showPassword = !showPassword"
                  >
                    <component :is="showPassword ? EyeOff : Eye" class="h-[18px] w-[18px]" />
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              class="mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-brand-navy text-sm font-semibold text-white shadow-lg shadow-brand-navy/25 transition hover:bg-brand-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-gray-900"
              :disabled="isLoading"
            >
              <LoaderCircle v-if="isLoading" class="h-5 w-5 animate-spin" />
              <span>{{ isLoading ? 'Autenticando...' : 'Entrar' }}</span>
              <ArrowRight v-if="!isLoading" class="h-4 w-4" />
            </button>

            <p class="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
              Não possui conta?
              <button type="button" class="font-semibold text-brand-navy transition hover:underline dark:text-brand-cyan" @click="handleRegister">
                Cadastre-se
              </button>
            </p>
          </form>
        </div>

        <div class="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-[11px] text-gray-500 dark:border-gray-800 dark:bg-gray-800/60 dark:text-gray-400">
          <ShieldCheck class="h-3.5 w-3.5 shrink-0 text-brand-cyan" />
          <span>Acesso restrito a colaboradores autorizados.</span>
        </div>
      </section>
    </div>

    <p class="relative z-10 mt-6 text-center text-[11px] text-gray-500 dark:text-gray-500">
      © {{ currentYear }} CCDTI — Centro Carioca de Diagnóstico e Tratamento por Imagem. Desenvolvido por Tecnologia da Informação.
    </p>

    <!-- Prompts de instalação (PWA) -->
    <div v-if="showInstallMessage" class="fixed inset-0 z-50 flex items-end justify-center bg-black/30 px-4 pb-8">
      <div class="w-full max-w-md animate-fade-in-up rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <div class="mb-3 flex items-center gap-3">
          <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/10 text-brand-navy dark:bg-brand-cyan/15 dark:text-brand-cyan">
            <Share class="h-5 w-5" />
          </span>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">Instalar aplicativo</h2>
        </div>
        <p class="mb-5 text-sm text-gray-600 dark:text-gray-400">
          Toque em <Share class="mx-0.5 inline-block h-4 w-4 align-text-bottom text-brand-navy dark:text-brand-cyan" /> e selecione
          <strong class="font-medium text-gray-800 dark:text-gray-200">"Adicionar à Tela de Início"</strong>.
        </p>
        <button type="button" class="btn w-full bg-brand-navy text-white hover:bg-brand-deep" @click="showInstallMessage = false">Entendi</button>
      </div>
    </div>

    <div v-if="showAndroidInstallMessage" class="fixed inset-0 z-50 flex items-end justify-center bg-black/30 px-4 pb-8">
      <div class="w-full max-w-md animate-fade-in-up rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <div class="mb-3 flex items-center gap-3">
          <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/10 text-brand-navy dark:bg-brand-cyan/15 dark:text-brand-cyan">
            <EllipsisVertical class="h-5 w-5" />
          </span>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">Instalar aplicativo</h2>
        </div>
        <p class="mb-5 text-sm text-gray-600 dark:text-gray-400">
          Toque em <EllipsisVertical class="mx-0.5 inline-block h-4 w-4 align-text-bottom text-brand-navy dark:text-brand-cyan" /> e selecione
          <strong class="font-medium text-gray-800 dark:text-gray-200">"Adicionar à Tela Inicial"</strong>.
        </p>
        <button type="button" class="btn w-full bg-brand-navy text-white hover:bg-brand-deep" @click="showAndroidInstallMessage = false">Entendi</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { login } from '@/services/authService'
import {
  Activity,
  ArrowRight,
  Building2,
  CircleAlert,
  CircleCheck,
  EllipsisVertical,
  Eye,
  EyeOff,
  LoaderCircle,
  Lock,
  Moon,
  ScanLine,
  Share,
  ShieldCheck,
  Sun,
  User,
} from 'lucide-vue-next'
import saudeLogo from '@/assets/SaudePublicaCarioca_Logo_HorizontalEstendida_Branco.png'
import logoCcdti from '@/assets/logo_ccdti.png'
import logoCcdtiBranco from '@/assets/logo_ccdti_branco.png'
import zegotinhaImage from '@/assets/zegotinha.png'

const router = useRouter()

const showPassword = ref(false)
const username = ref('')
const password = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isDark = ref(false)

const currentYear = new Date().getFullYear()

// Destaques do painel institucional (alinhados aos módulos da Home)
const features = [
  { icon: Building2, title: 'Unidades, setores e equipes', text: 'Gestão centralizada da rede e das unidades avançadas.' },
  { icon: ScanLine, title: 'Equipamentos e ativos', text: 'Monitoramento de tomógrafos, kits e ativos de TI.' },
  { icon: Activity, title: 'Indicadores e eventos adversos', text: 'Acompanhamento em tempo real da operação assistencial.' },
  { icon: ShieldCheck, title: 'Governança e pendências', text: 'Demandas judiciais, pendências e infraestrutura sob controle.' },
]

// Prompts de instalação (PWA)
const showInstallMessage = ref(false)
const showAndroidInstallMessage = ref(false)

const toggleDarkMode = () => {
  isDark.value = !isDark.value
  localStorage.setItem('darkMode', isDark.value)
  document.documentElement.classList.toggle('dark', isDark.value)
}

const handleLogin = async () => {
  if (!username.value || !password.value) {
    errorMessage.value = 'Por favor, preencha todos os campos'
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    await login(username.value, password.value)
    router.push('/home')
  } catch (error) {
    errorMessage.value = 'Usuário ou senha inválidos'
  } finally {
    isLoading.value = false
  }
}

const handleForgotPassword = () => {
  router.push({ name: 'ResetPassword' })
}

const handleRegister = () => {
  router.push({ name: 'Register' })
}

// Detecção de dispositivo para o prompt de instalação
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
const isInStandaloneMode = () => 'standalone' in navigator && navigator.standalone
const isAndroid = () => /Android/i.test(navigator.userAgent)

onMounted(() => {
  isDark.value = localStorage.getItem('darkMode') === 'true'
  document.documentElement.classList.toggle('dark', isDark.value)

  if (isIos() && !isInStandaloneMode()) {
    showInstallMessage.value = true
  }
  if (isAndroid() && !window.matchMedia('(display-mode: standalone)').matches) {
    showAndroidInstallMessage.value = true
  }

  if (router.currentRoute.value.query.accessDenied) {
    errorMessage.value = 'Acesso expirado. Por favor, faça login para continuar.'
    router.replace({ query: {} })
  }
})
</script>
